import { Divider, Grid, useEventCallback } from '@mui/material';
import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { __PERSON_API_URL__, __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { AccordionPersonForm as AccordionPersonFormVM } from 'shell/view-models/accordion-person-form';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { FamilyVM } from '../family/family-vm';
import NewRegistrationPersonModal from 'shell/components/shared/registration-person-sipo-modal';
import { PracticeFamily } from 'shell/models/practice-family';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';
import { transformPersonFamily } from 'practice/utils/transform-person-family';
import { useAireSubscription } from 'subscription/context/aire-subscription';
import FormGenerality from './generality';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import TablePerson from './table-person';
import { toast } from 'react-toastify';
import TableNewFamily from './table-new-family';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { NewPersonVM } from 'shell/components/shared/sipo-person-form/new-person';
import { SubjectData, SubjectsList } from 'shell/models/person';
import { transformGenerality } from 'shell/utils/consult-person/request-transformer/registry-office/generality';
import { Parenthood } from 'shell/models/person/parenthood';
import { transformProfessionTitle } from 'shell/utils/consult-person/request-transformer/other-datas/profession-title';
import { TypeRegister } from 'practice/constants/type-register';
import AccordionFilter from './accordion-filter';
import { DateTime } from 'luxon';
import { ResidenceOrigin } from 'shell/view-models/personal-data';
import { transformCitizenship } from 'shell/utils/consult-person/request-transformer/registry-office/citizenship';
import { Citizenship } from 'shell/models/person/citizenship';
import ResidenceOriginComponent from 'practice/components/edit/form/shared/residence-origin';

export interface PersonFormVM extends AccordionPersonFormVM {
  family: Array<FamilyVM>;
}

interface AccordionPersonFormProps {
  disabled?: boolean;
  values?: PersonFormVM;
  onChange: (key: keyof PersonFormVM, value: PersonFormVM[keyof PersonFormVM]) => void;
  codeEnte?: string;
  yearId?: string;
  ForPracticeTemporary?: boolean;
  typeRegister?: string;
  progressive?: string;
  typePractice?: string;
  idFamily?: string;
  onChangeFields: (values: PersonFormVM) => void;
  isMaiAnagrafato?: boolean;
}

const AccordionPersonFormComponent = ({
  disabled,
  values,
  onChange,
  codeEnte,
  yearId,
  typeRegister,
  progressive,
  ForPracticeTemporary,
  onChangeFields,
  isMaiAnagrafato,
}: AccordionPersonFormProps): React.ReactElement => {
  const navigate = useNavigate();
  const { intestatarioData, praticaKey } = useAireSubscription();

  const [openModalNewPerson, setOpenModalNewPerson] = useState({ open: false, id: '', idAnpr: '' });

  const [viewGenerality, setViewGenerality] = useState<FamilyVM | undefined>(undefined);

  const [viewListPerson, setViewListPerson] = useState<Array<FamilyVM>>([]);

  const [citizenshipValidityDate, setCitizenshipValidityDate] = useState<DateTime | null>(null);

  const insertData = useHttpClient(
    `${__PRACTICE_API_URL__}/visualizzazione-scelta-famiglia/inserisci/componente/${codeEnte}-${yearId}-${typeRegister}-${progressive}`,
    {
      options: { method: 'POST' },
    }
  );

  const deleteData = useHttpClient(
    `${__PRACTICE_API_URL__}/visualizzazione-scelta-famiglia/rimuovi`,
    {
      options: { method: 'DELETE' },
    }
  );

  const filterTable = useFilterTable<NewPersonVM>();

  // Reset degli stati locali quando si annulla o si torna dalla pagina edit
  useEffect(() => {
    // Se ci sono dati nel form ma praticaKey è null/undefined, significa che l'operazione è stata annullata
    // Oppure se siamo tornati senza salvare
    if (viewGenerality && !praticaKey) {
      setViewGenerality(undefined);
      setCitizenshipValidityDate(null);
    }

    if (filterTable.values?.generality && !praticaKey) {
      filterTable.setValues({
        ...filterTable.values,
        generality: undefined,
        currentStep: undefined,
      });
    }
  }, [praticaKey, viewGenerality, filterTable]);

  useEffect(() => {
    if (
      !filterTable.values?.generality ||
      filterTable.values?.currentStep !== 1 ||
      filterTable.values?.fromList
    ) {
      return;
    }

    const generalityNew: FamilyVM = {
      ...(filterTable.values?.generality || {}),
      surname: filterTable.values?.generality?.surname || '',
      name: filterTable.values?.generality?.name || '',
      fiscalCode: filterTable.values?.generality?.fiscalCode || '',
      birthDate: filterTable.values?.generality?.birthDate
        ? filterTable.values?.generality.birthDate.toFormat('dd/MM/yyyy')
        : '',
      birthplace: '',
      residence: '',
      relationship: '',
      drivingLicense: filterTable.values.drivingLicenseBooklet?.drivingLicense || '',
      aire: filterTable.values?.generality?.aire || '',
      auto: '',
      cfControl: '1',
      generality: filterTable.values?.generality
        ? {
            ...(filterTable.values?.generality?.generality || {}),
            ...filterTable.values?.generality,
          }
        : null,
      // residenceOrigin: filterTable.values?.
      subject: {
        generalita:
          transformGenerality(
            filterTable.values?.generality || {},
            {
              fiscalCode: filterTable.values?.generality?.fiscalCode,
              name: filterTable.values?.generality?.name,
              surname: filterTable.values?.generality?.surname,
              withoutSurname: filterTable.values?.generality?.withoutSurname,
              withoutName: filterTable.values?.generality?.withoutName,
            },
            {}
          ) || undefined,
        paternita: filterTable.values?.paternity?.generality
          ? ({
              generalita:
                transformGenerality(
                  filterTable.values?.paternity?.generality || {},
                  {
                    fiscalCode: filterTable.values?.paternity?.generality?.fiscalCode,
                    name: filterTable.values?.paternity?.generality?.name,
                    surname: filterTable.values?.paternity?.generality?.surname,
                    withoutSurname: filterTable.values?.paternity?.generality?.withoutSurname,
                    withoutName: filterTable.values?.paternity?.generality?.withoutName,
                  },
                  {}
                ) || undefined,
            } as Parenthood)
          : undefined,
        maternita: filterTable.values?.maternity?.generality
          ? ({
              generalita:
                transformGenerality(
                  filterTable.values?.maternity?.generality || {},
                  {
                    fiscalCode: filterTable.values?.maternity?.generality?.fiscalCode,
                    name: filterTable.values?.maternity?.generality?.name,
                    surname: filterTable.values?.maternity?.generality?.surname,
                    withoutSurname: filterTable.values?.maternity?.generality?.withoutSurname,
                    withoutName: filterTable.values?.maternity?.generality?.withoutName,
                  },
                  {}
                ) || undefined,
            } as Parenthood)
          : undefined,
        altriDati: transformProfessionTitle(filterTable.values?.professionTitle) || undefined,
        cittadinanza:
          (transformCitizenship(filterTable.values.citizenship) as Array<Citizenship>) || undefined,
      },
    };

    setViewGenerality(generalityNew);
  }, [
    filterTable.values?.generality,
    filterTable.values?.currentStep,
    filterTable.values?.fromList,
  ]);

  const onYesNewPerson = useEventCallback(() => {
    // set step
    filterTable.setValues({
      ...(filterTable.values || {}),
      currentStep: 1,
      fromList: false,
    });

    navigate(
      {
        pathname: generatePath('/iscrizioni/residenza/estero/persona-sipo'),
        search:
          openModalNewPerson.id || openModalNewPerson.idAnpr
            ? `?${createSearchParams({
                idIntestatario: openModalNewPerson.id || '0',
                idAnpr: openModalNewPerson.idAnpr || '0',
              })}`
            : '',
      },
      {
        state: {
          accountHolderFiscalCode: values?.fiscalCode || undefined,
          preservedIntestatarioData: intestatarioData,
        },
      }
    );
  });

  const getFamilyMembers = useHttpClient<HttpResponse<SubjectsList>, Array<FamilyVM>>(
    `${__PERSON_API_URL__}/non-residenti/ricerca-intestatario-cittadinanza-aire`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformPersonFamily,
    }
  );

  const onSearch = useEventCallback(() => {
    setViewGenerality(undefined);
    setCitizenshipValidityDate(null);

    setViewListPerson([]);

    const data = {
      tipoRegistro: typeRegister,
      idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
      progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      datiRichiesta: {
        ricercaNazionale: '1', // fixed
      },
      criteriRicerca: {
        codiceFiscale: values?.fiscalCode || undefined,
        nome: values?.name || undefined,
        cognome: values?.surname || undefined,
      },
    };

    (
      getFamilyMembers({
        data,
      }) as ResponsePromise<HttpResponseList<PracticeFamily>, Array<FamilyVM>>
    ).then((response) => {
      const family = response as Array<FamilyVM>;

      if (family?.length === 0) {
        // soggetto non trovato
        setOpenModalNewPerson({ open: true, id: '', idAnpr: '' });
      }

      if (family?.length === 1) {
        if (family[0].residenceOrigin?.irreperibile) {
          setOpenModalNewPerson({
            open: true,
            id: family[0].generality?.municipalityIdentifier || '',
            idAnpr: family[0].generality?.anprIdentifier || '',
          });
        } else if (family[0].isSipo) {
          setOpenModalNewPerson({
            open: true,
            id: family[0].generality?.municipalityIdentifier || '',
            idAnpr: '',
          });
        }
        // trovato un solo soggetto
        setViewGenerality(family[0]);
        const firstDateStr = (family[0]?.subject as SubjectData)?.cittadinanza?.[0]?.dataValidita;
        setCitizenshipValidityDate(firstDateStr ? DateTime.fromISO(firstDateStr) : null);
      }

      if (family?.length > 1) {
        // trovati più soggetti
        setViewListPerson(family);
      }
      //onChange('family', family);
    });
  });

  const onFieldGeneralityChange = useEventCallback(
    (
      key: keyof AccordionGeneralityForm,
      value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
    ) => {
      onChange('generality', {
        ...(values?.generality || {}),
        [key]: value,
      });
    }
  );

  const onFieldsGeneralityChange = useEventCallback((valuesGenerality: AccordionGeneralityForm) => {
    onChange('generality', {
      ...(values?.generality || {}),
      ...valuesGenerality,
    });
  });

  const onChangeResidence = useEventCallback(
    (key: keyof ResidenceOrigin, value: ResidenceOrigin[keyof ResidenceOrigin]) => {
      onChange('residenceOrigin', {
        ...(values?.residenceOrigin || {}),
        [key]: value,
      });

      setViewGenerality({
        ...(viewGenerality || {}),
        residenceOrigin: {
          ...(viewGenerality?.residenceOrigin || {}),
          [key]: value,
        },
      } as FamilyVM);
    }
  );

  const onChangeValuesResidence = useEventCallback((valuesResidence: ResidenceOrigin) => {
    onChange('residenceOrigin', {
      ...(values?.residenceOrigin || {}),
      ...valuesResidence,
    });

    setViewGenerality({
      ...(viewGenerality || {}),
      residenceOrigin: {
        ...(viewGenerality?.residenceOrigin || {}),
        ...(valuesResidence || {}),
      },
    } as FamilyVM);
  });

  const onVisibilityPressPerson = useEventCallback((personSelected: FamilyVM) => {
    setViewListPerson([]);

    setViewGenerality(personSelected);
    const firstDateStrSelected = (personSelected?.subject as SubjectData)?.cittadinanza?.[0]
      ?.dataValidita;
    setCitizenshipValidityDate(
      firstDateStrSelected ? DateTime.fromISO(firstDateStrSelected) : null
    );
  });

  const onAddPerson = useEventCallback(() => {
    if (!Boolean(viewGenerality)) {
      return;
    }

    // Se residenceOrigin non è compilato, usa il luogo di nascita come fallback
    const birthplace = (viewGenerality?.subject as SubjectData)?.generalita?.luogoNascita;
    const residenceOriginOrBirthplace: ResidenceOrigin | undefined =
      viewGenerality?.residenceOrigin ||
      (birthplace
        ? {
            isForeign: !!birthplace?.localita,
            origin: birthplace?.comune
              ? {
                  id: birthplace?.comune?.codiceIstat || '',
                  description: birthplace?.comune?.nomeComune || '',
                  province: birthplace?.comune?.siglaProvinciaIstat || '',
                }
              : null,
            originState: birthplace?.localita
              ? {
                  id: birthplace?.localita?.codiceStato || '',
                  value: birthplace?.localita?.descrizioneStato || '',
                  province: birthplace?.localita?.provinciaContea || '',
                }
              : null,
            originMunicipalityForeign: birthplace?.localita
              ? {
                  id: birthplace?.localita?.codiceStato || '',
                  description: birthplace?.localita?.descrizioneLocalita || '',
                  province: birthplace?.localita?.provinciaContea || '',
                }
              : null,
            irreperibile: false,
            neverRegistered: false,
            previousOriginCode: undefined,
            previousOriginSipoCode: undefined,
          }
        : undefined);

    const personData = {
      tipoDatiRispostaEstrazioneSoggettiVO: {
        ...(viewGenerality?.subject || {}),
        generalita: viewGenerality?.subject?.generalita
          ? {
              ...viewGenerality?.subject?.generalita,
              luogoNascita: viewGenerality?.subject?.generalita?.luogoNascita
                ? {
                    ...viewGenerality.subject?.generalita?.luogoNascita,
                    localita: viewGenerality.subject?.generalita?.luogoNascita?.localita
                      ? {
                          ...viewGenerality.subject?.generalita?.luogoNascita?.localita,
                          provinciaContea:
                            viewGenerality.generality?.foreign?.municipality?.el?.cdNazione ||
                            viewGenerality.generality?.foreign?.municipality?.province ||
                            undefined,
                        }
                      : undefined,
                  }
                : undefined,
            }
          : null,
        cittadinanza:
          ((viewGenerality?.subject as SubjectData)?.cittadinanza || [])?.length > 0
            ? {
                ...((viewGenerality?.subject as SubjectData)?.cittadinanza || [])[0],
                dataValidita: citizenshipValidityDate
                  ? citizenshipValidityDate.toISODate()
                  : ((viewGenerality?.subject as SubjectData)?.cittadinanza || [])[0]?.dataValidita,
              }
            : undefined,
      },
      irreperibile: residenceOriginOrBirthplace?.irreperibile,
      comune:
        residenceOriginOrBirthplace && !residenceOriginOrBirthplace?.isForeign
          ? {
              nomeComune: residenceOriginOrBirthplace?.origin?.description,
              codiceIstat: residenceOriginOrBirthplace?.origin?.id,
              siglaProvinciaIstat: residenceOriginOrBirthplace?.origin?.province,
            }
          : undefined,
      localita:
        residenceOriginOrBirthplace && residenceOriginOrBirthplace?.isForeign
          ? {
              descrizioneStato: residenceOriginOrBirthplace?.originState?.value,
              codiceStato: residenceOriginOrBirthplace?.originState?.id,
              provinciaContea: residenceOriginOrBirthplace?.originState?.province,
              descrizioneLocalita:
                residenceOriginOrBirthplace?.originMunicipalityForeign?.description,
            }
          : undefined,
      comunePrecedente:
        residenceOriginOrBirthplace?.previousOriginCode ||
        residenceOriginOrBirthplace?.previousOriginSipoCode
          ? {
              codice: residenceOriginOrBirthplace?.previousOriginCode,
              codiceSipo: residenceOriginOrBirthplace?.previousOriginSipoCode,
            }
          : undefined,
    };

    (insertData({ data: personData }) as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');

      const newBody: Array<FamilyVM> = (values?.family || []).concat([viewGenerality as FamilyVM]);

      onChange('family', newBody);

      // reset stato locale
      setViewGenerality(undefined);
      setCitizenshipValidityDate(null);

      // reset stato generale (questo stato è settato solo se inseriamo NUOVA PERSONA)
      filterTable.setValues({
        ...(filterTable.values || {}),
        generality: undefined,
      });
    });
  });

  const onDeletePress = useEventCallback((person: FamilyVM, index: number) => {
    const data = {
      key: {
        codiceEnte: codeEnte || undefined,
        tipoRegistro: typeRegister || undefined,
        idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
        progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      },
      codFiscale: person.fiscalCode || undefined,
    };

    (deleteData({ data }) as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');

      const newBody: Array<FamilyVM> = (values?.family || []).filter(
        (_, currentIndex) => currentIndex !== index
      );

      onChange('family', newBody);
    });
  });

  const disabledAddPerson = useMemo(() => {
    if (!(typeRegister === TypeRegister.Immigration || typeRegister === TypeRegister.Emigration)) {
      return false;
    }
  }, [typeRegister]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <AccordionFilter
          onChange={onChange}
          onChangeFields={onChangeFields}
          onSearch={onSearch}
          disabled={disabled}
          values={values}
        />
        <NewRegistrationPersonModal
          open={openModalNewPerson.open}
          id={openModalNewPerson.id}
          idAnpr={openModalNewPerson.idAnpr}
          onClose={() => setOpenModalNewPerson({ open: false, id: '', idAnpr: '' })}
          onNo={() => setOpenModalNewPerson({ open: false, id: '', idAnpr: '' })}
          onYes={() => onYesNewPerson()}
        />
      </Grid>
      {Boolean(viewGenerality) ? (
        <Fragment>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <FormGenerality
              disabled
              ForPracticeTemporary={ForPracticeTemporary}
              onChangeGenerality={onFieldGeneralityChange}
              onChangeFieldsGenerality={onFieldsGeneralityChange}
              generality={viewGenerality?.generality || undefined}
              labelMunicipalityItalian="Comune di nascita"
              labelProvinceItalian="Provincia di nascita"
              citizenshipValidityDate={citizenshipValidityDate}
              onChangeCitizenshipDate={(date) => setCitizenshipValidityDate(date)}
              cittadinanza={(viewGenerality?.subject as SubjectData)?.cittadinanza?.[0]}
            />
          </Grid>
          {typeRegister === TypeRegister.Immigration && !isMaiAnagrafato ? (
            <Grid item xs={12}>
              <ResidenceOriginComponent
                onChange={onChangeResidence}
                onChangeValues={onChangeValuesResidence}
                required
                values={viewGenerality?.residenceOrigin}
                fromDate={DateTime.now()}
              />
            </Grid>
          ) : undefined}
        </Fragment>
      ) : undefined}
      {(viewListPerson || []).length > 0 ? (
        <Grid item xs={12}>
          <TablePerson data={viewListPerson} onVisibilityPress={onVisibilityPressPerson} />
        </Grid>
      ) : undefined}
      <Grid
        container
        spacing={2}
        item
        xs={12}
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        <Grid item xs={12} md={10}>
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            id="handle-search-anpr"
            fullWidth
            onClick={onAddPerson}
            disabled={!Boolean(viewGenerality) || disabledAddPerson}
          >
            {"Aggiungi persona che si sposta con l'intestatario"}
          </PmiButton>
        </Grid>
      </Grid>
      {(values?.family || []).length > 0 ? (
        <Grid item xs={12}>
          <TableNewFamily
            onChange={(family) => onChange('family', family)}
            values={values?.family || []}
            disabled={disabled}
            onDeletePress={onDeletePress}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

AccordionPersonFormComponent.displayName = 'Accordion person form component';

const AccordionPersonForm = memo(AccordionPersonFormComponent);

export default AccordionPersonForm;
